import { Route, useLocation } from "wouter";
import { Layout } from "@/components/ui/layout/Layout";
import Login from "@/components/Login";
import ForcePasswordChange from "@/components/ForcePasswordChange";
import ForceEnroll2FA from "@/components/ForceEnroll2FA";
import { useRole, type UserRole } from "@/hooks/useRole";
import { useCarrierSync } from "@/hooks/useCarrierSync";
import { useQueryClient } from "@tanstack/react-query";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SessionTimeoutWarning } from "@/components/SessionTimeoutWarning";

import { BrokerRoutes, BrokerDashboard } from "./brokerRoutes";
import { EmployerRoutes, EmployerDashboard } from "./employerRoutes";
import { EmployeeRoutes, EmployeeDashboard } from "./employeeRoutes";
import { SuperadminRoutes, SuperadminDashboard } from "./superadminRoutes";
import { GARoutes, GADashboardComponent } from "./gaRoutes";
import { SharedRoutes } from "./sharedRoutes";
import { PublicRoutes } from "./publicRoutes";

// Routes that don't require authentication
const PUBLIC_ROUTES = ['/embed/forms', '/forgot-password', '/reset-password', '/quote', '/proposals', '/proposal/'];

function isPublicRoute(path: string): boolean {
  return PUBLIC_ROUTES.some(route => path.startsWith(route));
}

function getDashboardComponent(role: UserRole) {
  switch (role) {
    case "superadmin":
      return SuperadminDashboard;
    case "employer":
      return EmployerDashboard;
    case "employee":
      return EmployeeDashboard;
    case "ga":
      return GADashboardComponent;
    default:
      return BrokerDashboard;
  }
}

/**
 * Neutral splash while /api/auth/user is in flight. Deliberately NOT wrapped
 * in <Layout>: the header's "Sign In" control keyed off `!isAuthenticated`,
 * which is false until the session resolves, so a slow lookup (notably while
 * impersonating) looked exactly like being logged out (BUG-008 / DEV-320).
 */
function AuthLoadingSplash() {
  return (
    <div
      data-testid="auth-loading"
      className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-950"
    >
      <div className="text-center">
        <img src="/logo.png" alt="administr" className="h-10 w-auto mx-auto mb-6" />
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
        <p className="mt-4 text-sm text-muted-foreground">Loading your session…</p>
      </div>
    </div>
  );
}

/**
 * Protected routes component - handles authentication and role-based routing
 * Separated to ensure hooks are always called (React rules of hooks)
 */
function ProtectedRoutes() {
  const queryClient = useQueryClient();
  const { currentRole, isLoading, isAuthenticated, user } = useRole();

  // Automatically sync carriers from Word & Brown API on app load
  useCarrierSync();

  // For protected routes, check authentication
  if (isLoading) {
    return <AuthLoadingSplash />;
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  // Check if user needs to change password. Suppressed while impersonating —
  // an admin entering a not-yet-onboarded account shouldn't be forced to set
  // that account's password (mirrors the server guard in /api/auth/user).
  if ((user as any)?.requiresPasswordChange && !(user as any)?.isImpersonating) {
    return (
      <ForcePasswordChange
        onSuccess={() => {
          // Invalidate user query to refetch updated user data
          queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
          window.location.reload();
        }}
      />
    );
  }

  // M4: privileged-role enforcement — superadmin/broker without 2FA can't
  // reach any non-enrollment endpoint. Force them through enrollment.
  if ((user as any)?.mustEnroll2fa) {
    return <ForceEnroll2FA />;
  }

  const DashboardComponent = getDashboardComponent(currentRole);

  return (
    <>
      {/* Idle-session watchdog: warns at 18 min of inactivity, logs out at 20.
          Must live INSIDE ProtectedRoutes (post-auth) but OUTSIDE Layout so a
          layout refactor can't silently drop it again — that's exactly how this
          regressed (mounted in f1301be4, lost in a later routes refactor). */}
      <SessionTimeoutWarning />
      <Layout>
        {/* Dashboard route */}
        <Route path="/" component={DashboardComponent} />

        {/* Role-based routes - wrapped in ErrorBoundary so crashes don't take down the whole app */}
        <ErrorBoundary>
          {currentRole === "superadmin" && <SuperadminRoutes />}
          {currentRole === "broker" && <BrokerRoutes />}
          {currentRole === "employer" && <EmployerRoutes />}
          {currentRole === "employee" && <EmployeeRoutes />}
          {currentRole === "ga" && <GARoutes />}

          {/* Shared routes */}
          <SharedRoutes />
        </ErrorBoundary>
      </Layout>
    </>
  );
}

/**
 * Main app routes - handles public vs protected route separation
 * Public routes (forgot-password, reset-password, etc.) don't require authentication
 */
export function AppRoutes() {
  const [location] = useLocation();

  // Public routes don't need authentication - render them directly
  if (isPublicRoute(location)) {
    return <PublicRoutes />;
  }

  // Protected routes need authentication
  return <ProtectedRoutes />;
}
